/**
 * Tasks page view model.
 *
 * The page is backed by the real personal-task API in services/calendar
 * (`/api/calendar/tasks/`, #1307 — the same rows the email module's "My Tasks"
 * pane reads). That model has two statuses (pending / completed), three
 * priorities (high / medium / low), a calendar due date and a description.
 * The design's richer concepts (Hubs, Squads, assignees, tags, subtasks,
 * comments, extra statuses, Urgent priority) have no backend yet and are
 * deliberately not represented here.
 */

import type { TaskCard } from "./task/api";

export type TaskStatus = "TO DO" | "COMPLETE";
export type TaskPriority = "High" | "Normal" | "Low";

export type ApiTaskStatus = "pending" | "completed";
export type ApiTaskPriority = "urgent" | "high" | "medium" | "low";

export interface ApiTask {
  id: string;
  title: string;
  description: string;
  due_date: string | null;
  priority: ApiTaskPriority;
  status: ApiTaskStatus;
  source_email_thread_id: string;
  personal_list?: string | null;
  created_at: string;
  updated_at: string;
  /** The full card (Epic 4) — labels, subtasks, files, reminder, repeat. */
  card?: TaskCard;
}

export interface TaskItem {
  id: string;
  title: string;
  description: string;
  status: TaskStatus;
  priority: TaskPriority;
  /** ISO calendar date (YYYY-MM-DD) or null. */
  dueDate: string | null;
  createdAt: string;
  updatedAt: string;
  sourceEmailThreadId: string;
  /** US 4.12 — the personal list it sits in, if any. */
  personalList: string | null;
  card?: TaskCard;
}

export interface TaskDraft {
  title: string;
  description?: string;
  status?: TaskStatus;
  priority?: TaskPriority;
  dueDate?: string | null;
  personalList?: string | null;
  /** Hub › Squad to create the task in; omitted = a private task. */
  hubId?: string | null;
  squadId?: string | null;
  /** Squad members' emails (hub tasks only — a private task has no assignees). */
  assignees?: string[];
  /** Label names (hub tasks only); an unknown name becomes a new label. */
  labels?: string[];
}

export type TaskPatch = Partial<Omit<TaskItem, "id" | "createdAt" | "updatedAt" | "sourceEmailThreadId" | "card">>;

export const STATUSES: TaskStatus[] = ["TO DO", "COMPLETE"];
export const PRIORITIES: TaskPriority[] = ["High", "Normal", "Low"];

/** Classes copied from the design's STATUS_META, including the `.wc-st*` glass hooks. */
export const STATUS_META: Record<TaskStatus, { dot: string; chip: string }> = {
  "TO DO": {
    dot: "bg-slate-400 wc-st-dot wc-st--todo",
    chip: "wc-st wc-st--todo bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200",
  },
  COMPLETE: {
    dot: "bg-green-500 wc-st-dot wc-st--done",
    chip: "wc-st wc-st--done bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300",
  },
};

/** Classes copied from the design's PRIORITY_META, including the `.wc-pri*` glass hooks. */
export const PRIORITY_META: Record<TaskPriority, { color: string }> = {
  High: { color: "text-orange-500 wc-pri--high" },
  Normal: { color: "text-blue-500 wc-pri--normal" },
  Low: { color: "text-slate-400 wc-pri--low" },
};

const PRIORITY_FROM_API: Record<ApiTaskPriority, TaskPriority> = {
  // My tasks has three priorities; an Urgent task (Epic 4) reads as High here.
  urgent: "High",
  high: "High",
  medium: "Normal",
  low: "Low",
};
const PRIORITY_TO_API: Record<TaskPriority, ApiTaskPriority> = {
  High: "high",
  Normal: "medium",
  Low: "low",
};

export function fromApi(t: ApiTask): TaskItem {
  return {
    id: t.id,
    title: t.title,
    description: t.description ?? "",
    status: t.status === "completed" ? "COMPLETE" : "TO DO",
    priority: PRIORITY_FROM_API[t.priority] ?? "Normal",
    dueDate: t.due_date ?? null,
    createdAt: t.created_at,
    updatedAt: t.updated_at,
    sourceEmailThreadId: t.source_email_thread_id ?? "",
    personalList: t.personal_list ?? null,
    card: t.card,
  };
}

export function toApiPatch(p: TaskPatch | TaskDraft): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (p.title !== undefined) out.title = p.title;
  if (p.description !== undefined) out.description = p.description;
  if (p.status !== undefined) out.status = p.status === "COMPLETE" ? "completed" : "pending";
  if (p.priority !== undefined) out.priority = PRIORITY_TO_API[p.priority];
  if (p.dueDate !== undefined) out.due_date = p.dueDate;
  if (p.personalList !== undefined) out.personal_list = p.personalList;
  return out;
}

// ── Dates ────────────────────────────────────────────────────────────────────

export type DueBucket = "Overdue" | "Today" | "Tomorrow" | "This Week" | "Next Week" | "Later" | "No date";
export const DUE_BUCKETS: DueBucket[] = ["Overdue", "Today", "Tomorrow", "This Week", "Next Week", "Later", "No date"];

export type DateQuickPick = "Today" | "Tomorrow" | "This Week" | "Next Week";
export const DATE_QUICK_PICKS: DateQuickPick[] = ["Today", "Tomorrow", "This Week", "Next Week"];

export function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function parseIsoDate(iso: string): Date {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

/** Monday-based week start. */
function startOfWeek(d: Date): Date {
  const s = startOfDay(d);
  const dow = (s.getDay() + 6) % 7;
  return addDays(s, -dow);
}

/** Resolves a design quick pick ("This Week" etc.) to a real calendar date. */
export function quickPickDate(pick: DateQuickPick, now = new Date()): string {
  const today = startOfDay(now);
  switch (pick) {
    case "Today":
      return toIsoDate(today);
    case "Tomorrow":
      return toIsoDate(addDays(today, 1));
    case "This Week":
      return toIsoDate(addDays(startOfWeek(today), 4)); // Friday of this week
    case "Next Week":
      return toIsoDate(addDays(startOfWeek(today), 7)); // Monday of next week
  }
}

export function dueBucket(iso: string | null, now = new Date()): DueBucket {
  if (!iso) return "No date";
  const due = parseIsoDate(iso).getTime();
  const today = startOfDay(now);
  const t0 = today.getTime();
  if (due < t0) return "Overdue";
  if (due === t0) return "Today";
  if (due === addDays(today, 1).getTime()) return "Tomorrow";
  const weekStart = startOfWeek(today);
  if (due < addDays(weekStart, 7).getTime()) return "This Week";
  if (due < addDays(weekStart, 14).getTime()) return "Next Week";
  return "Later";
}

/** Short, friendly label for a due date: Today / Tomorrow / Yesterday / "Oct 24". */
export function formatDue(
  iso: string | null,
  t: (key: string, opts: { defaultValue: string }) => string,
  locale?: string,
  now = new Date(),
): string {
  if (!iso) return t("tasks.due.noDateShort", { defaultValue: "No date" });
  const due = parseIsoDate(iso);
  const today = startOfDay(now);
  const diff = Math.round((due.getTime() - today.getTime()) / 86_400_000);
  if (diff === 0) return t("tasks.due.today", { defaultValue: "Today" });
  if (diff === 1) return t("tasks.due.tomorrow", { defaultValue: "Tomorrow" });
  if (diff === -1) return t("tasks.due.yesterday", { defaultValue: "Yesterday" });
  const sameYear = due.getFullYear() === today.getFullYear();
  return due.toLocaleDateString(locale, {
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
  });
}

export function timeAgo(
  iso: string,
  t: (key: string, opts: Record<string, unknown>) => string,
  now = Date.now(),
): string {
  const diff = Math.max(0, now - new Date(iso).getTime());
  const m = Math.floor(diff / 60000);
  if (m < 1) return t("tasks.time.justNow", { defaultValue: "Just now" });
  if (m < 60) return t("tasks.time.minutesAgo", { defaultValue: "{{count}}m ago", count: m });
  const h = Math.floor(m / 60);
  if (h < 24) return t("tasks.time.hoursAgo", { defaultValue: "{{count}}h ago", count: h });
  const d = Math.floor(h / 24);
  if (d < 7) return t("tasks.time.daysAgo", { defaultValue: "{{count}}d ago", count: d });
  const w = Math.floor(d / 7);
  if (w < 5) return t("tasks.time.weeksAgo", { defaultValue: "{{count}}w ago", count: w });
  return new Date(iso).toLocaleDateString();
}

// ── Sorting ──────────────────────────────────────────────────────────────────

export type SortKey = "title" | "status" | "priority" | "due";
export interface SortState {
  key: SortKey;
  dir: "asc" | "desc";
}

const SORT_ACCESSORS: Record<SortKey, (t: TaskItem) => string | number> = {
  title: (t) => t.title.toLowerCase(),
  status: (t) => STATUSES.indexOf(t.status),
  // Design order is most-urgent first; "desc" therefore puts High on top.
  priority: (t) => PRIORITIES.length - PRIORITIES.indexOf(t.priority),
  due: (t) => t.dueDate ?? "9999-12-31",
};

export function sortTasks(list: TaskItem[], sort: SortState | null): TaskItem[] {
  if (!sort) return list;
  const acc = SORT_ACCESSORS[sort.key];
  const dir = sort.dir === "asc" ? 1 : -1;
  return [...list].sort((a, b) => {
    const av = acc(a);
    const bv = acc(b);
    if (av < bv) return -dir;
    if (av > bv) return dir;
    return 0;
  });
}

// ── Filtering ────────────────────────────────────────────────────────────────

export type Resolution = "Unresolved" | "Resolved" | "Completed";
export const RESOLUTIONS: Resolution[] = ["Unresolved", "Resolved", "Completed"];

export interface BarFilters {
  status: TaskStatus[];
  priority: TaskPriority[];
  resolution: Resolution[];
  due: DueBucket[];
}

export const FB_EMPTY: BarFilters = { status: [], priority: [], resolution: [], due: [] };

export function normalizeFilters(raw: unknown): BarFilters {
  const src = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const pick = <T extends string>(key: keyof BarFilters, allowed: readonly T[]): T[] => {
    const v = src[key];
    return Array.isArray(v) ? (v.filter((x) => allowed.includes(x as T)) as T[]) : [];
  };
  return {
    status: pick("status", STATUSES),
    priority: pick("priority", PRIORITIES),
    resolution: pick("resolution", RESOLUTIONS),
    due: pick("due", DUE_BUCKETS),
  };
}

function resolutionsOf(t: TaskItem): Resolution[] {
  return t.status === "COMPLETE" ? ["Resolved", "Completed"] : ["Unresolved"];
}

export function matchesBarFilters(t: TaskItem, bf: BarFilters, now = new Date()): boolean {
  if (bf.status.length && !bf.status.includes(t.status)) return false;
  if (bf.priority.length && !bf.priority.includes(t.priority)) return false;
  if (bf.resolution.length && !resolutionsOf(t).some((r) => bf.resolution.includes(r))) return false;
  if (bf.due.length && !bf.due.includes(dueBucket(t.dueDate, now))) return false;
  return true;
}

export function countBarFilters(bf: BarFilters): number {
  return bf.status.length + bf.priority.length + bf.resolution.length + bf.due.length;
}

/** Search across title, description and id. */
export function taskSearchMatch(t: TaskItem, q: string): boolean {
  if (!q) return true;
  const s = q.toLowerCase();
  return [t.title, t.description, t.id].join(" ").toLowerCase().includes(s);
}

export type GroupBy = "Status" | "Priority";
